import React, { useCallback } from 'react';
import { fileTypeFromBuffer } from 'file-type';
import { MAX_UPLOAD_FILE_SIZE_BYTES } from '@soundxyz/utils/constants/constants';
import { useEditProfile } from '@components/Profile/Edit/form/useEditProfile';
import { ImageCropModal } from '@components/modals/ImageCrop/ImageCropModal';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { MediaType } from '@graphql/reactQuery';
import { ACCEPTED_IMAGE_TYPES } from '@utils/constants';
import { uploadFileAndReturnKey } from '@utils/s3';

type UseImageUploadReturnType = {
  selectImage: () => void;
};

export const useUpdateCoverImage = ({
  onConfirm,
  onSuccess,
  onDone,
}: {
  onConfirm?: () => void;
  onSuccess?: () => void;
  onDone?: () => void;
}): UseImageUploadReturnType => {
  const { openToast } = ToastContext.useContainer();
  const { openModal } = ModalContext.useContainer();
  const { setField, onSave } = useEditProfile();

  const saveCroppedImage = useCallback(
    async (selectedFile: File) => {
      if (!selectedFile) return;
      onConfirm?.();
      try {
        const key = await uploadFileAndReturnKey(selectedFile, MediaType.ArtistBannerImage);
        if (key) {
          setField('coverImageUploadKey', key);
          await onSave();
          onSuccess?.();
        } else {
          throw new Error('No key returned from upload');
        }
      } catch (err) {
        openToast({
          text: 'There was an error uploading your cover image. Please try again.',
          variant: 'error',
        });
      } finally {
        onDone?.();
      }
    },
    [onConfirm, setField, onSave, onSuccess, openToast, onDone],
  );

  const showAvatarCropDialog = useCallback(
    async (selectedFile: File) => {
      const fileUrl = URL.createObjectURL(selectedFile);
      const buffer = await selectedFile.arrayBuffer();
      const uint8Array = new Uint8Array(buffer);
      const fileType = await fileTypeFromBuffer(uint8Array);

      if (fileType?.mime && !Object.keys(ACCEPTED_IMAGE_TYPES).includes(fileType.mime)) {
        openToast({
          text: 'Images can only be JPG and PNG. Please try again.',
          variant: 'error',
        });
        return;
      }

      openModal(ModalType.IMAGE_CROP, {
        body: (
          <ImageCropModal
            modalTitle="Crop Cover"
            blobURL={fileUrl}
            setBlob={(b: Blob | null) => {
              if (!b) return;
            }}
            saveImageMutation={saveCroppedImage}
            closeOnUpload
            handleReselectImage={selectImage}
            forCover
          />
        ),
      });
    },
    [openModal, openToast, saveCroppedImage], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const selectImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = Object.keys(ACCEPTED_IMAGE_TYPES).join(',');
    input.onchange = event => {
      const target = event.target as HTMLInputElement;
      const selectedFile = target?.files?.[0];
      if (selectedFile && selectedFile.size > 0 && selectedFile.size < MAX_UPLOAD_FILE_SIZE_BYTES) {
        showAvatarCropDialog(selectedFile);
      }
    };
    input.click();
  };

  return { selectImage };
};
